import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query. SSR/test-safe (falls back to `false` when
 * `matchMedia` is unavailable). Re-renders when the match state flips.
 */
export function useMediaQuery(query: string): boolean {
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState(read);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * The ONE number that separates a workstation from a handheld, in px. It is Tailwind's `md` breakpoint, so
 * `useIsDesktop`/`useIsHandheld` and every `md:` class in the markup agree by construction.
 */
export const DESKTOP_MIN_WIDTH_PX = 768;

/** True at Tailwind's `md` breakpoint and up (≥768px) — the desktop two-pane layout. */
export const useIsDesktop = (): boolean => useMediaQuery(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`);

/**
 * HANDHELD — the single source of truth for "this device cannot run the creation workflow" (RF3-DEVICE:
 * desktop is the workshop, mobile is the viewer).
 *
 * Deliberately NOT user-agent sniffing, which misreads tablets, desktop browsers in a narrow window, and
 * every new device shape. Two CAPABILITY facts decide it instead: the viewport is below the desktop
 * breakpoint AND the primary pointer is coarse (a finger). Both must hold — a narrow desktop window keeps a
 * fine pointer and stays a workstation, and a touchscreen laptop stays a workstation because it is wide.
 */
export const useIsHandheld = (): boolean =>
  useMediaQuery(`(max-width: ${DESKTOP_MIN_WIDTH_PX - 1}px) and (pointer: coarse)`);
